/**
 * RoyalePuzzleGridPanel — the LEFT panel of the redesigned PuzzleRoyaleBattle
 * (web). Replaces the old single-row RoyaleStrip on the player side. Layout,
 * top → bottom:
 *
 *   1. Live combo badge  ("x1" dim → "x2/x3…" lit) — the CURRENT streak, resets
 *      to x1 (dim) on a wrong solve. Matches the mobile combo pill.
 *   2. Correct / Wrong counts in a bordered box.
 *   3. A 3-column grid of puzzle boxes (1…total; MAX_PUZZLES is a safety
 *      ceiling, not the expected length — the grid grows with the actual
 *      sequence, so a 200-puzzle custom set renders 200 boxes). 3 columns are
 *      visible; the strip scrolls HORIZONTALLY to columns 4-7, and the scrollbar
 *      only becomes reachable once >30 puzzles are done (before that the grid is
 *      pinned to the first 30 = 3 cols × 10 rows). Each box:
 *        - grey + number  = pending
 *        - darker (current) = the puzzle in play right now
 *        - green + white  = solved correctly
 *        - red + white    = solved wrong
 *      Hovering a COMPLETED box pops a ~250px board (classroom CompletedPuzzle
 *      size) beside it, showing that puzzle's starting FEN.
 *   4. Forfeit (leave) button pinned at the bottom.
 *
 * Presentational: the battle screen derives `outcomes` / `currentIndex` /
 * `combo` / counts (from my move stream, or the followed player's trail when
 * spectating) and a `fenForIndex` resolver, and passes them in. This component
 * owns no royale state.
 */
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Animated, Easing, Pressable, StyleSheet, View} from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';
// TRIAL (2026-07-09): royale-wide board swap to the new chessground board used
// by PlayChess. This usage is the read-only hover-preview board in the grid.
// Legacy revert target: import ChessboardWithArrow from '@/Components/ChessboardWithArrow';
import ChessboardWithArrow from '@/Components/Chessboard';
import useChessboardColorTheme from '@/Hooks/ChessboardColorTheme/useChessboardColorTheme';

const AnimatedMCI = Animated.createAnimatedComponent(MaterialCommunityIcons);

export type PuzzleOutcome = 'correct' | 'wrong' | 'current' | 'pending';

// Two meters, driven by the combo (consecutive solves):
//   • COMBO BARS — 5 segments, one lit at each bonus-ladder threshold where the
//     points-per-solve steps up: combo 1, 3, 5, 10, 15.
//   • STREAK TILE FILL — a 0→1 progress on the badge showing how far the combo
//     has advanced from the LAST lit bar's threshold toward the NEXT one. When
//     it reaches 1, the next bar lights and the tile resets to 0.
// So: combo 1 → bar1 lit, tile resets (next target 3). combo 2 → tile 50% (1 of
// 2 toward 3). combo 3 → bar2 lit, tile resets. …matching the real ladder.
const COMBO_THRESHOLDS = [1, 3, 5, 10, 15];
const STREAK_SEGMENTS = COMBO_THRESHOLDS.length;

/** How many combo bars are lit at this combo (thresholds reached). */
function litComboBars(combo: number): number {
  let n = 0;
  for (const t of COMBO_THRESHOLDS) {
    if (combo >= t) n += 1;
  }
  return n;
}

/** Streak-tile fill 0..1: progress from the last reached threshold toward the
 *  next one. 0 at combo 0; snaps to a fresh fill each time a bar lights. */
function streakTileFill(combo: number): number {
  if (combo <= 0) return 0;
  const lit = litComboBars(combo);
  if (lit >= COMBO_THRESHOLDS.length) return 1; // maxed
  const prev = lit > 0 ? COMBO_THRESHOLDS[lit - 1] : 0;
  const next = COMBO_THRESHOLDS[lit];
  const span = next - prev;
  if (span <= 0) return 1;
  return Math.max(0, Math.min(1, (combo - prev) / span));
}

// Solved = the Rematch/Race button teal so the grid matches those controls.
// Wrong = the app red.
const GREEN = '#3DAB9E'; // matches the Rematch / Race-tab button green
const RED = '#EF5350'; // CCColors.Red — wrong
// Pending box fill: a medium warm grey (was very light #E3E1D9) so the WHITE
// upcoming number reads with enough contrast (user: white numbers everywhere).
const GREY = '#BDB9AC';
const GREY_TEXT = '#8A8677';
const CURRENT = '#C7B48A'; // darker highlight for the in-play puzzle
const BROWN = CCColors.Primary?.Brown ?? '#4D3B22';

// Grid geometry. Puzzles laid out ROW-MAJOR — 3 per row (1,2,3 across; 4,5,6
// next row; …). 10 rows (30 boxes) are visible; the strip scrolls VERTICALLY
// to later rows, and that scroll only engages once >30 puzzles are done.
//
// Safety ceiling only — NOT the expected sequence length. It used to be 70 (the
// engine's default sequence), which silently clipped the grid for any longer
// set: a host-uploaded custom tournament of 200 puzzles rendered 70 boxes and
// read as "only 70 puzzles are in the race", even though all 200 were served and
// playable. The grid is fully derived from `count` (rows = ceil(count/COLS), and
// the scroller is flex-sized with auto-scroll off `currentRow`), so it scales on
// its own; this bound exists purely so a nonsense `total` can't try to render
// tens of thousands of boxes. Kept at the upload ceiling
// (MAX_PUZZLES_PER_UPLOAD in chess/fun_challenges/pgn_puzzles.py) so a custom
// set can never be clipped, with headroom for the 300-puzzle sequence cap the
// game service allows.
const MAX_PUZZLES = 300;
const COLS = 3;
const VISIBLE_ROWS = 10;
const FREE_SCROLL_AFTER = 30; // 10 rows × 3 cols — scroll unlocks only past this
const CELL = 34;
const CELL_GAP = 6;
const HOVER_BOARD = 250; // classroom CompletedPuzzle board size

interface Props {
  /** Per-index outcome, length = number of issued puzzles (<= MAX_PUZZLES). */
  outcomes: PuzzleOutcome[];
  /** 0-based index of the puzzle currently in play (highlighted). */
  currentIndex: number;
  /** Current live combo (1 = no streak). */
  combo: number;
  solved: number;
  wrong: number;
  /** Resolve a box index → that puzzle's starting FEN (for the hover preview). */
  fenForIndex: (index: number) => string | undefined;
  /** Total puzzles issued (drives how many boxes render). Bounded by MAX_PUZZLES
   *  only as a safety ceiling — normal and custom sequences render in full. */
  total: number;
  showForfeit: boolean;
  onForfeit: () => void;
  /** Whose progress this is — 'You' for the player, the name when spectating. */
  ownerLabel?: string;
  /**
   * POST-GAME review: when set, clicking a COMPLETED (correct/wrong) box calls
   * this with its index so the parent loads that puzzle on the main board. Only
   * passed once the race has ended — click-to-review is disabled during play.
   * `selectedIndex` highlights the box currently being reviewed.
   */
  onSelect?: (index: number) => void;
  selectedIndex?: number | null;
}

export default function RoyalePuzzleGridPanel({
  outcomes,
  currentIndex,
  combo,
  solved,
  wrong,
  fenForIndex,
  total,
  showForfeit,
  onForfeit,
  ownerLabel,
  onSelect,
  selectedIndex = null,
}: Props) {
  const chessboardColorTheme = useChessboardColorTheme();
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [hoverTop, setHoverTop] = useState(0);

  // Lit as soon as the current streak has ANY solve, dim on reset to 0.
  const comboLit = combo >= 1;
  const bars = litComboBars(combo);

  // ── Streak animation ──────────────────────────────────────────────────────
  // On each solve (combo increases): a gold bolt DROPS from ~8px above and
  // merges into the badge (which pulses), then the tile fill animates to its
  // new level. When a bar lights the fill resets to a fresh climb.
  const prevCombo = useRef(combo);
  const dropAnim = useRef(new Animated.Value(0)).current; // 0 idle → 1 dropped
  const badgePulse = useRef(new Animated.Value(1)).current;
  const fillAnim = useRef(new Animated.Value(streakTileFill(combo))).current;

  useEffect(() => {
    const increased = combo > prevCombo.current;
    prevCombo.current = combo;

    // Settle the tile fill to its new level. On a solve it climbs SLOWLY (600ms)
    // AFTER the bolt lands so the rise is clearly visible; on reset it snaps back.
    Animated.timing(fillAnim, {
      toValue: streakTileFill(combo),
      duration: increased ? 720 : 220,
      delay: increased ? 240 : 0,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false, // height % can't use native driver
    }).start();

    if (!increased) return;

    // Falling bolt: fade+drop in, then it "merges" (fades out at the badge) as
    // the badge pulses.
    dropAnim.setValue(0);
    Animated.sequence([
      Animated.timing(dropAnim, {
        toValue: 1,
        duration: 384,
        easing: Easing.in(Easing.quad),
        useNativeDriver: true,
      }),
    ]).start(() => dropAnim.setValue(0));
    Animated.sequence([
      Animated.timing(badgePulse, {
        toValue: 1.14,
        duration: 150,
        delay: 180,
        easing: Easing.out(Easing.quad),
        useNativeDriver: true,
      }),
      Animated.spring(badgePulse, {
        toValue: 1,
        friction: 4,
        useNativeDriver: true,
      }),
    ]).start();
  }, [combo, dropAnim, badgePulse, fillAnim]);

  const count = Math.min(Math.max(total, 0), MAX_PUZZLES);
  const completed = solved + wrong;
  // Vertical scroll only engages once past the first 30 (10 visible rows); before
  // that the strip is pinned so it can't scroll into emptiness.
  const scrollEnabled = completed > FREE_SCROLL_AFTER;

  // Row-major matrix: 3 boxes per row numbered across (row r, col c) = r*COLS+c,
  // so the grid reads 1,2,3 / 4,5,6 / … top-to-bottom. Rows = ceil(count/COLS).
  const boxes = useMemo(() => {
    const rows = Math.ceil(count / COLS);
    const grid: (number | null)[][] = [];
    for (let r = 0; r < rows; r++) {
      const row: (number | null)[] = [];
      for (let c = 0; c < COLS; c++) {
        const idx = r * COLS + c;
        row.push(idx < count ? idx : null);
      }
      grid.push(row);
    }
    return grid;
  }, [count]);

  const outcomeFor = (idx: number): PuzzleOutcome => {
    if (idx === currentIndex) return 'current';
    return outcomes[idx] ?? 'pending';
  };

  const hoverFen = hoverIndex != null ? fenForIndex(hoverIndex) : undefined;
  // Orient the preview to the side that's TO MOVE in that puzzle (the player's
  // perspective) — not always white (#1). The post-setup FEN's active-colour
  // field (index 1) is 'w' or 'b'.
  const hoverOrientation: 'white' | 'black' =
    hoverFen && hoverFen.split(' ')[1] === 'b' ? 'black' : 'white';

  return (
    <View style={styles.panel}>
      {/* 1. Streak badge (animated fill + falling-bolt-on-solve) + COMBO bars. */}
      <View style={styles.comboWrap}>
        <Animated.View
          style={[
            styles.comboBadge,
            comboLit && styles.comboBadgeLit,
            {transform: [{scale: badgePulse}]},
          ]}>
          {/* Animated fill rising from the bottom of the tile (streak progress
              toward the next combo bar). */}
          <Animated.View
            style={[
              styles.comboFill,
              {
                height: fillAnim.interpolate({
                  inputRange: [0, 1],
                  outputRange: ['0%', '100%'],
                }),
              },
            ]}
            pointerEvents="none"
          />
          <View style={styles.comboInline}>
            {/* Fun Challenges lightning-bolt glyph (#2), ~80% of the number. */}
            <MaterialCommunityIcons
              name="lightning-bolt"
              size={32}
              color={comboLit ? BROWN : GREY_TEXT}
              style={styles.comboBolt}
            />
            <CCText style={[styles.comboText, comboLit && styles.comboTextLit]}>
              {combo}
            </CCText>
          </View>
        </Animated.View>

        {/* Falling bolt — drops from ~16px above and merges into the badge on
            each solve. Gold, fades as it lands. Sits OUTSIDE the (clipped)
            badge so the drop-in is visible; centered over the badge. */}
        <AnimatedMCI
          name="lightning-bolt"
          size={28}
          color="#F0B429"
          pointerEvents="none"
          style={[
            styles.fallingBolt,
            {
              opacity: dropAnim.interpolate({
                inputRange: [0, 0.15, 0.75, 1],
                outputRange: [0, 1, 1, 0],
              }),
              transform: [
                {
                  translateY: dropAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [-6, 34],
                  }),
                },
              ],
            },
          ]}
        />
        <CCText style={styles.comboLabel}>Streak</CCText>

        {/* COMBO bars — "Combo" label left of the 5 segments (#1); each fills
            left→right with an animation when it lights (#2). */}
        <View style={styles.comboBarRow}>
          <CCText style={styles.comboBarLabel}>Combo</CCText>
          <View style={styles.streakBar}>
            {Array.from({length: STREAK_SEGMENTS}).map((_, i) => (
              <ComboSegment key={i} filled={i < bars} index={i} />
            ))}
          </View>
        </View>
      </View>

      {/* 2. Correct / Wrong box */}
      <View style={styles.countsBox}>
        <View style={styles.countCell}>
          <CCText style={[styles.countValue, {color: GREEN}]}>{solved}</CCText>
          <CCText style={styles.countLabel}>Correct</CCText>
        </View>
        <View style={styles.countDivider} />
        <View style={styles.countCell}>
          <CCText style={[styles.countValue, {color: RED}]}>{wrong}</CCText>
          <CCText style={styles.countLabel}>Wrong</CCText>
        </View>
      </View>

      {/* 3. Row-major grid (3/row), 10 rows visible, vertical scroll post-30.
          Auto-scrolls to keep the CURRENT puzzle's row in view (#4). */}
      <View style={styles.gridWrap}>
        <ScrollableGrid
          scrollEnabled={scrollEnabled}
          currentRow={Math.floor(currentIndex / COLS)}>
          {boxes.map((row, r) => (
            <View key={`r${r}`} style={styles.gridRow}>
              {row.map((idx, c) => {
                if (idx == null) {
                  return <View key={`e${r}-${c}`} style={styles.cellSpacer} />;
                }
                const outcome = outcomeFor(idx);
                // Hoverable = completed OR the current puzzle (#4) — as long as
                // a FEN is resolvable for it. Pending future puzzles have none.
                const hoverable =
                  (outcome === 'correct' ||
                    outcome === 'wrong' ||
                    outcome === 'current') &&
                  !!fenForIndex(idx);
                // Reviewable (post-game click): completed OR the current/last
                // puzzle (any box with a resolvable FEN), only when the parent
                // enabled review (onSelect set = race ended).
                const reviewable =
                  !!onSelect &&
                  (outcome === 'correct' ||
                    outcome === 'wrong' ||
                    outcome === 'current') &&
                  !!fenForIndex(idx);
                const isSelected = selectedIndex === idx;
                return (
                  <Pressable
                    key={`b${idx}`}
                    onPress={reviewable ? () => onSelect!(idx) : undefined}
                    // NOTE: do NOT set `disabled` when not reviewable — a disabled
                    // Pressable stops firing onHoverIn/Out, which killed the
                    // in-game hover preview. No onPress = already non-interactive.
                    // Hover on web maps to onHoverIn/onHoverOut (RNW).
                    onHoverIn={
                      hoverable
                        ? () => {
                            setHoverIndex(idx);
                            setHoverTop(r * (CELL + CELL_GAP));
                          }
                        : undefined
                    }
                    onHoverOut={
                      hoverable ? () => setHoverIndex(null) : undefined
                    }
                    style={[
                      styles.cell,
                      outcome === 'pending' && styles.cellPending,
                      outcome === 'current' && styles.cellCurrent,
                      outcome === 'correct' && styles.cellCorrect,
                      outcome === 'wrong' && styles.cellWrong,
                      reviewable && styles.cellReviewable,
                      isSelected && styles.cellSelected,
                    ]}>
                    <CCText
                      style={[
                        styles.cellText,
                        (outcome === 'correct' || outcome === 'wrong') &&
                          styles.cellTextDone,
                      ]}>
                      {idx + 1}
                    </CCText>
                  </Pressable>
                );
              })}
            </View>
          ))}
        </ScrollableGrid>

        {/* Hover preview — a ~250px board at the hovered puzzle's start FEN,
            floated to the RIGHT of the grid, vertically aligned to the box. */}
        {hoverIndex != null && hoverFen ? (
          <View
            style={[styles.hoverCard, {top: clampHoverTop(hoverTop)}]}
            pointerEvents="none">
            <View style={{width: HOVER_BOARD}}>
              <ChessboardWithArrow
                position={hoverFen}
                boardOrientation={hoverOrientation}
                colorTheme={chessboardColorTheme}
                // New board: read-only via `disabled`. Hide the a–h/1–8 edge
                // labels — on this small preview thumbnail they're just clutter.
                // Wrapped in pointerEvents:none anyway, so it's a pure preview.
                disabled
                coordinates={false}
                animationDuration={0}
              />
            </View>
            <CCText style={styles.hoverCaption}>
              {/* When spectating we show WHOSE puzzle it is; for your own grid
                  the bare puzzle number reads cleaner (no "· You"). */}
              Puzzle {hoverIndex + 1}
              {ownerLabel && ownerLabel !== 'You' ? ` · ${ownerLabel}` : ''}
            </CCText>
          </View>
        ) : null}
      </View>

      {/* 4. Forfeit */}
      {showForfeit ? (
        <Pressable onPress={onForfeit} style={styles.forfeitBtn}>
          <CCText style={styles.forfeitText}>Forfeit</CCText>
        </Pressable>
      ) : null}
    </View>
  );
}

// Keep the hover board on-screen: don't let it float above the grid top.
function clampHoverTop(top: number): number {
  return Math.max(0, top - HOVER_BOARD / 2 + CELL / 2);
}

/**
 * One combo-bar segment. Grey track with a gold fill that sweeps LEFT→RIGHT
 * (animated width 0→100%) when the segment becomes lit, and collapses back when
 * the streak resets (#2). A tiny stagger by index makes a cascade as multiple
 * light at once.
 */
function ComboSegment({filled, index}: {filled: boolean; index: number}) {
  const w = useRef(new Animated.Value(filled ? 1 : 0)).current;
  useEffect(() => {
    Animated.timing(w, {
      toValue: filled ? 1 : 0,
      duration: filled ? 720 : 200,
      delay: filled ? index * 96 : 0,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false, // animating width %
    }).start();
  }, [filled, index, w]);
  return (
    <View style={styles.streakSeg}>
      <Animated.View
        style={[
          styles.streakSegFill,
          {
            width: w.interpolate({
              inputRange: [0, 1],
              outputRange: ['0%', '100%'],
            }),
          },
        ]}
      />
    </View>
  );
}

/**
 * Vertical scroller sized to exactly VISIBLE_ROWS rows (30 boxes). When
 * scrollEnabled is false the content is pinned to the first 30; the bar only
 * appears/engages once >30 puzzles are done, revealing later rows.
 */
function ScrollableGrid({
  scrollEnabled,
  currentRow,
  children,
}: {
  scrollEnabled: boolean;
  currentRow: number;
  children: React.ReactNode;
}) {
  const {ScrollView} = require('react-native');
  const ref = useRef<any>(null);
  const rowH = CELL + CELL_GAP;

  // Auto-scroll so the current puzzle's row stays visible once it passes the
  // first window — keep it ~2 rows from the bottom so upcoming rows show (#4).
  useEffect(() => {
    if (!scrollEnabled || !ref.current) return;
    const target = Math.max(0, (currentRow - (VISIBLE_ROWS - 2)) * rowH);
    ref.current.scrollTo?.({y: target, animated: true});
  }, [currentRow, scrollEnabled, rowH]);

  // Scroll whenever content might not fit — either past the free window (>30
  // done) OR the viewport is too short to show all rows. We can't measure
  // overflow cheaply, so just always allow scrolling; the bar is what we gate on
  // showing, but dragging is always available so the LAST row is never trapped
  // half-clipped (the bottom-cutoff bug).
  return (
    <ScrollView
      ref={ref}
      scrollEnabled
      showsVerticalScrollIndicator={scrollEnabled}
      // Fill EXACTLY the grid area (flex:1) — do NOT force a fixed maxHeight
      // taller than the available space, or the last row clips. When the area
      // is shorter than 10 rows, the grid simply scrolls.
      style={{flex: 1, alignSelf: 'stretch'}}
      contentContainerStyle={{
        flexDirection: 'column',
        // Center the box rows; equal L/R padding so the scrollbar gutter on the
        // right doesn't pull the grid off-centre.
        alignItems: 'center',
        paddingHorizontal: 10,
        paddingBottom: 6,
      }}>
      {children}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  panel: {
    flex: 1,
    // Participate in the column's flex shrink so the grid caps and nothing
    // spills past the card bottom (#2).
    minHeight: 0,
    paddingVertical: 12,
    paddingHorizontal: 10,
    alignItems: 'center',
    gap: 12,
    // White card background (#3) so the grid reads as its own panel.
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#EDE4CC',
    // Let the hover popover overflow the card and stack above the board.
    overflow: 'visible',
    zIndex: 1000,
  },
  comboWrap: {alignItems: 'center', gap: 6, alignSelf: 'stretch', position: 'relative'},
  comboBadge: {
    width: 96,
    height: 96,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(246,236,119,0.35)',
    // relative + hidden so the rising streak-fill is clipped to the tile.
    position: 'relative',
    overflow: 'hidden',
  },
  comboBadgeLit: {backgroundColor: '#F6EC77'},
  // Streak progress fill rising from the bottom of the tile (animated height).
  comboFill: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(240,180,41,0.30)',
  },
  // Lightning bolt (~80% of the number) vertically centered next to it (#2).
  comboInline: {flexDirection: 'row', alignItems: 'center', zIndex: 2},
  comboBolt: {marginRight: 2},
  comboText: {fontSize: FontSizes[40] || 40, fontWeight: '800', color: GREY_TEXT},
  // The falling gold bolt (centered over the badge) that merges in on a solve.
  fallingBolt: {position: 'absolute', top: 0, alignSelf: 'center', zIndex: 5},
  comboLabel: {
    fontSize: FontSizes[13] || 13,
    fontWeight: '800',
    color: GREY_TEXT,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  // "Combo" label to the left of the 5 bars (#1).
  comboBarRow: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'stretch',
    gap: 8,
    paddingHorizontal: 4,
  },
  comboBarLabel: {
    fontSize: FontSizes[11] || 11,
    fontWeight: '800',
    color: GREY_TEXT,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  // Segmented combo bar: grey track, gold fill sweeps in left→right (#2).
  streakBar: {flexDirection: 'row', gap: 4, flex: 1},
  streakSeg: {
    flex: 1,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#E3E1D9',
    overflow: 'hidden',
  },
  streakSegFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    borderRadius: 3,
    backgroundColor: '#F0B429', // same gold as the streak fill
  },
  comboTextLit: {color: BROWN},

  countsBox: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'stretch',
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.10)',
    borderRadius: 10,
    paddingVertical: 8,
  },
  countCell: {flex: 1, alignItems: 'center'},
  countDivider: {width: 1, height: 28, backgroundColor: 'rgba(0,0,0,0.08)'},
  countValue: {fontSize: FontSizes[18] || 18, fontWeight: '800'},
  countLabel: {fontSize: FontSizes[11] || 11, color: GREY_TEXT, fontWeight: '600'},

  // overflow visible + raised zIndex so the hover board can float OUT of the
  // panel and OVER the center board column.
  gridWrap: {
    flex: 1,
    // minHeight:0 lets this flex child shrink BELOW its content so the grid
    // scroller caps correctly and the Forfeit/last rows aren't pushed off (#2).
    minHeight: 0,
    alignSelf: 'stretch',
    alignItems: 'center',
    overflow: 'visible',
    zIndex: 1000,
  },
  gridRow: {flexDirection: 'row', gap: CELL_GAP, marginBottom: CELL_GAP},
  cell: {
    width: CELL,
    height: CELL,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cellSpacer: {width: CELL, height: CELL},
  cellPending: {backgroundColor: GREY},
  cellCurrent: {backgroundColor: CURRENT, borderWidth: 2, borderColor: BROWN},
  cellCorrect: {backgroundColor: GREEN},
  cellWrong: {backgroundColor: RED},
  // Post-game reviewable box: subtle affordance (pointer cursor on web via
  // Pressable) + a distinct TEAL ring when it's the one on the review board —
  // NOT brown (the 'current' box already uses a brown border, so a brown
  // selection ring was indistinguishable from it).
  cellReviewable: {cursor: 'pointer' as any},
  cellSelected: {
    borderWidth: 2.5,
    borderColor: '#1F6C63',
    shadowColor: '#1F6C63',
    shadowOpacity: 0.4,
    shadowRadius: 4,
  },
  // Upcoming/pending numbers in white too (user: all royale types) — reads
  // cleaner and consistent with the solved/wrong boxes' white numbers.
  cellText: {fontSize: FontSizes[13] || 13, fontWeight: '700', color: '#FFFFFF'},
  cellTextDone: {color: '#FFFFFF'},

  hoverCard: {
    position: 'absolute',
    left: '100%',
    marginLeft: 10,
    padding: 8,
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOpacity: 0.18,
    shadowRadius: 12,
    shadowOffset: {width: 0, height: 4},
    // Must sit ABOVE the center board column (a sibling that renders later in
    // the DOM). A large zIndex + elevation escapes that stacking on web/native.
    elevation: 24,
    zIndex: 1000,
  },
  hoverCaption: {
    marginTop: 6,
    fontSize: FontSizes[12] || 12,
    fontWeight: '700',
    color: BROWN,
    textAlign: 'center',
  },

  forfeitBtn: {
    alignSelf: 'stretch',
    backgroundColor: '#CD2C39',
    borderRadius: 10,
    paddingVertical: 10,
    alignItems: 'center',
    marginTop: 'auto',
  },
  forfeitText: {fontSize: FontSizes[14] || 14, fontWeight: '800', color: '#FFFFFF'},
});
